<template>
  <div class="step-card">
    <!-- Заголовок -->
    <div class="step-header">
      <div class="step-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="m12 3 2.6 5.3 5.9.9-4.3 4.2 1 5.9-5.2-2.8-5.2 2.8 1-5.9-4.3-4.2 5.9-.9L12 3Z"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <div class="step-heading">
        <span class="step-label">
          Последний шаг
        </span>

        <h3 class="step-title">
          Преимущества компании
        </h3>
      </div>

      <span class="optional-badge">
        Необязательно
      </span>
    </div>

    <p class="description">
      Выберите преимущества вашей компании. Мы покажем их кандидатам,
      чтобы выделить вас среди других работодателей.
    </p>

    <!-- Панель выбора -->
    <div class="selection-panel">
      <div class="selection-info">
        <div
          class="selection-counter"
          :class="{ active: selectedCount > 0 }"
        >
          <Transition name="counter" mode="out-in">
            <span
              :key="selectedCount"
              class="counter-value"
            >
              {{ selectedCount }}
            </span>
          </Transition>

          <span class="counter-text">
            {{ benefitsWord }}
          </span>
        </div>

        <span class="selection-hint">
          Рекомендуем выбрать 3–6 пунктов
        </span>
      </div>

      <Transition name="clear">
        <button
          v-if="selectedCount > 0"
          type="button"
          class="clear-button"
          @click="clearBenefits"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6.5 6.5 13.5 13.5M13.5 6.5 6.5 13.5"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
          </svg>

          Очистить
        </button>
      </Transition>
    </div>

    <!-- Преимущества -->
    <div class="benefits-grid">
      <button
        v-for="(perk, index) in allBenefits"
        :key="perk.id"
        type="button"
        class="perk"
        :class="{
          selected: isSelected(perk.id)
        }"
        :style="{
          '--animation-delay': `${index * 25}ms`
        }"
        :aria-pressed="isSelected(perk.id)"
        @click="toggle(perk.id)"
      >
        <span class="perk-emoji">
          {{ perk.emoji }}
        </span>

        <span class="perk-name">
          {{ perk.ruName }}
        </span>

        <span class="perk-check">
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="m5.5 10.2 2.8 2.8 6.2-6.2"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>

        <span class="perk-shine"></span>
      </button>
    </div>

    <!-- Результат -->
    <Transition name="summary" mode="out-in">
      <div
        v-if="selectedCount > 0"
        key="selected"
        class="selection-summary"
      >
        <div class="summary-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="m6.5 12.2 3.4 3.4 7.6-7.6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />

            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              stroke-width="1.7"
            />
          </svg>
        </div>

        <div class="summary-content">
          <strong>
            Отличный выбор!
          </strong>

          <span>
            Эти преимущества появятся в профиле вашей компании.
          </span>
        </div>
      </div>

      <div
        v-else
        key="empty"
        class="selection-summary neutral"
      >
        <div class="summary-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              stroke-width="1.7"
            />

            <path
              d="M12 11v5M12 7.5v.2"
              stroke="currentColor"
              stroke-width="1.9"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <div class="summary-content">
          <strong>
            Этот шаг можно пропустить
          </strong>

          <span>
            Преимущества можно будет добавить позже.
          </span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import {
  computed,
  watch
} from "vue";

const model = defineModel({
  required: true
});

const emit = defineEmits([
  "valid",
  "not-valid"
]);

const allBenefits = [
  {
    id: "01",
    emoji: "🌴",
    ruName: "Оплачиваемые отгулы"
  },
  {
    id: "02",
    emoji: "🕐",
    ruName: "Гибкий график работы"
  },
  {
    id: "03",
    emoji: "🏋️",
    ruName: "Абонемент в спортзал"
  },
  {
    id: "04",
    emoji: "🎉",
    ruName: "Укороченные дни перед праздниками"
  },
  {
    id: "05",
    emoji: "👶",
    ruName: "Оплачиваемый декрет"
  },
  {
    id: "06",
    emoji: "🥗",
    ruName: "Холодильник с едой"
  },
  {
    id: "07",
    emoji: "🎊",
    ruName: "Корпоративные мероприятия"
  },
  {
    id: "08",
    emoji: "🤝",
    ruName: "Волонтёрские дни"
  },
  {
    id: "09",
    emoji: "🐾",
    ruName: "Pet-friendly офис"
  },
  {
    id: "10",
    emoji: "🎓",
    ruName: "Оплачиваемое обучение"
  },
  {
    id: "11",
    emoji: "🏠",
    ruName: "Удалённая работа"
  },
  {
    id: "12",
    emoji: "🚇",
    ruName: "Оплачиваемый проезд"
  },
  {
    id: "13",
    emoji: "🧸",
    ruName: "Помощь по уходу за детьми"
  },
  {
    id: "14",
    emoji: "🔄",
    ruName: "Программа переквалификации"
  },
  {
    id: "15",
    emoji: "🎮",
    ruName: "Игровая комната"
  },
  {
    id: "16",
    emoji: "🩺",
    ruName: "Медицинская страховка"
  },
  {
    id: "17",
    emoji: "⚡️",
    ruName: "Внутренние сервисы"
  }
];

initializeBenefits();

/*
  Шаг необязательный — он всегда валиден.
*/
emit("valid");

const selectedCount = computed(() =>
  model.value.benefits.length
);

const benefitsWord = computed(() => {
  const count = selectedCount.value;
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  if (
    lastDigit === 1 &&
    lastTwoDigits !== 11
  ) {
    return "преимущество";
  }

  if (
    [2, 3, 4].includes(lastDigit) &&
    ![12, 13, 14].includes(lastTwoDigits)
  ) {
    return "преимущества";
  }

  return "преимуществ";
});

function initializeBenefits() {
  if (!Array.isArray(model.value.benefits)) {
    model.value.benefits = [];
    return;
  }

  /*
    Удаляем дубликаты и неизвестные старые значения.
  */
  const validIDs = new Set(
    allBenefits.map(item => item.id)
  );

  model.value.benefits = [
    ...new Set(model.value.benefits)
  ].filter(id => validIDs.has(id));
}

function isSelected(id) {
  return model.value.benefits.includes(id);
}

function toggle(id) {
  if (isSelected(id)) {
    model.value.benefits =
      model.value.benefits.filter(
        benefitID => benefitID !== id
      );
  } else {
    model.value.benefits = [
      ...model.value.benefits,
      id
    ];
  }

  emit("valid");
}

function clearBenefits() {
  model.value.benefits = [];
  emit("valid");
}

watch(
  () => model.value.benefits,
  () => {
    emit("valid");
  },
  {
    deep: true
  }
);
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.step-card {
  width: 100%;
  padding: 28px;

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(48, 132, 255, 0.075),
      transparent 250px
    ),
    #ffffff;

  animation: card-appearance 0.45s ease both;
}

/* Header */

.step-header {
  display: flex;
  align-items: center;
  gap: 13px;

  margin-bottom: 18px;
}

.step-icon {
  width: 44px;
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #36a1ff 0%,
      #4078f3 55%,
      #625bea 100%
    );

  border-radius: 14px;

  box-shadow:
    0 8px 18px rgba(48, 126, 246, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.24);
}

.step-icon svg {
  width: 23px;
  height: 23px;
}

.step-heading {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}

.step-label {
  color: #8b94a4;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.step-title {
  margin: 0;

  color: #20242c;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 19px;
  line-height: 1.25;
}

.optional-badge {
  flex-shrink: 0;

  padding: 7px 10px;

  color: #687385;
  background: rgba(113, 125, 145, 0.08);

  border: 1px solid rgba(113, 125, 145, 0.1);
  border-radius: 999px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
}

.description {
  max-width: 700px;

  margin: 0 0 22px;

  color: #697282;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  line-height: 1.65;
}

/* Selection panel */

.selection-panel {
  width: 100%;
  max-width: 840px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  margin-bottom: 15px;
}

.selection-info {
  min-width: 0;

  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.selection-counter {
  min-height: 30px;

  display: inline-flex;
  align-items: center;
  gap: 4px;

  padding: 6px 10px;

  color: #798291;
  background: #f3f5f8;

  border: 1px solid #e8ebef;
  border-radius: 10px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;

  transition:
    color 0.22s ease,
    background-color 0.22s ease,
    border-color 0.22s ease;
}

.selection-counter.active {
  color: #397fdb;
  background: rgba(57, 133, 235, 0.08);
  border-color: rgba(57, 133, 235, 0.12);
}

.counter-value {
  min-width: 10px;

  color: inherit;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 13px;
  text-align: center;
}

.selection-hint {
  color: #949ba7;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

.clear-button {
  display: flex;
  align-items: center;
  gap: 5px;

  padding: 7px 10px;

  cursor: pointer;

  color: #838b98;
  background: transparent;

  border: 1px solid #e1e4e9;
  border-radius: 9px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;
}

.clear-button:hover {
  color: #d75055;
  background: rgba(215, 80, 85, 0.06);
  border-color: rgba(215, 80, 85, 0.16);

  transform: translateY(-1px);
}

.clear-button svg {
  width: 14px;
  height: 14px;
}

/* Benefits */

.benefits-grid {
  width: 100%;
  max-width: 840px;

  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.perk {
  --brand-color: #348af7;

  position: relative;

  min-height: 46px;

  display: inline-flex;
  align-items: center;
  gap: 8px;

  overflow: hidden;

  padding: 9px 13px;

  cursor: pointer;

  color: #4e5868;
  background: #f7f8fa;

  border: 1px solid #e2e5ea;
  border-radius: 14px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.3;
  white-space: nowrap;

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.7);

  opacity: 0;

  animation:
    perk-appearance 0.38s ease forwards;

  animation-delay: var(--animation-delay);

  transition:
    color 0.23s ease,
    background-color 0.23s ease,
    border-color 0.23s ease,
    box-shadow 0.23s ease,
    transform 0.23s ease;
}

.perk:hover {
  color: #326ec4;
  background: #ffffff;
  border-color: rgba(52, 138, 247, 0.28);

  box-shadow:
    0 7px 18px rgba(39, 103, 192, 0.08);

  transform: translateY(-2px);
}

.perk:active {
  transform: translateY(0) scale(0.98);
}

.perk:focus-visible {
  outline: none;

  box-shadow:
    0 0 0 4px rgba(52, 138, 247, 0.14);
}

.perk.selected {
  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3495fa 0%,
      #397dea 58%,
      #5368e7 100%
    );

  border-color: transparent;

  box-shadow:
    0 8px 18px rgba(49, 119, 231, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);

  transform: translateY(-1px);
}

.perk.selected:hover {
  color: #ffffff;

  box-shadow:
    0 11px 23px rgba(49, 119, 231, 0.25);

  transform: translateY(-3px);
}

.perk-emoji {
  position: relative;
  z-index: 2;

  width: 26px;
  height: 26px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  background: #ffffff;

  border: 1px solid rgba(30, 50, 80, 0.06);
  border-radius: 8px;

  font-size: 14px;

  box-shadow:
    0 3px 8px rgba(20, 35, 55, 0.05);

  transition:
    background-color 0.22s ease,
    border-color 0.22s ease,
    transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.perk:hover .perk-emoji {
  transform: rotate(-4deg) scale(1.06);
}

.perk.selected .perk-emoji {
  background: rgba(255, 255, 255, 0.16);
  border-color: rgba(255, 255, 255, 0.15);

  box-shadow: none;

  transform: scale(1.05);
}

.perk-name {
  position: relative;
  z-index: 2;
}

.perk-check {
  position: relative;
  z-index: 2;

  width: 20px;
  height: 20px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-left: 1px;

  color: #ffffff;
  background: rgba(255, 255, 255, 0.18);

  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;

  opacity: 0;
  transform: scale(0.45);

  transition:
    opacity 0.22s ease,
    transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.perk.selected .perk-check {
  opacity: 1;
  transform: scale(1);
}

.perk-check svg {
  width: 13px;
  height: 13px;
}

.perk-shine {
  position: absolute;
  top: -50%;
  left: -70%;
  z-index: 1;

  width: 35%;
  height: 200%;

  pointer-events: none;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );

  transform: rotate(20deg);

  transition:
    left 0.65s ease;
}

.perk.selected:hover .perk-shine {
  left: 130%;
}

/* Summary */

.selection-summary {
  width: 100%;
  max-width: 840px;

  display: flex;
  align-items: center;
  gap: 11px;

  margin-top: 22px;
  padding: 13px 14px;

  color: #397450;

  background:
    linear-gradient(
      135deg,
      rgba(47, 171, 104, 0.075),
      rgba(43, 151, 116, 0.045)
    );

  border: 1px solid rgba(44, 159, 95, 0.13);
  border-radius: 13px;
}

.selection-summary.neutral {
  color: #667084;

  background: rgba(53, 132, 247, 0.055);

  border-color: rgba(53, 132, 247, 0.1);
}

.summary-icon {
  width: 33px;
  height: 33px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #2ca065;
  background: rgba(44, 160, 101, 0.1);

  border-radius: 10px;
}

.selection-summary.neutral .summary-icon {
  color: #4388f5;
  background: rgba(67, 136, 245, 0.09);
}

.summary-icon svg {
  width: 19px;
  height: 19px;
}

.summary-content {
  min-width: 0;

  display: flex;
  flex-direction: column;
  gap: 2px;
}

.summary-content strong {
  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;
}

.summary-content span {
  color: #7f8b83;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  line-height: 1.4;
}

.selection-summary.neutral .summary-content span {
  color: #87909e;
}

/* Animations */

@keyframes card-appearance {
  from {
    opacity: 0;
    transform: translateY(7px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes perk-appearance {
  from {
    opacity: 0;

    transform:
      translateY(7px)
      scale(0.97);
  }

  to {
    opacity: 1;

    transform:
      translateY(0)
      scale(1);
  }
}

.counter-enter-active,
.counter-leave-active,
.clear-enter-active,
.clear-leave-active,
.summary-enter-active,
.summary-leave-active {
  transition:
    opacity 0.23s ease,
    transform 0.23s ease;
}

.counter-enter-from {
  opacity: 0;

  transform: translateY(-5px);
}

.counter-leave-to {
  opacity: 0;

  transform: translateY(5px);
}

.clear-enter-from,
.clear-leave-to {
  opacity: 0;

  transform: translateX(6px);
}

.summary-enter-from {
  opacity: 0;

  transform: translateY(7px);
}

.summary-leave-to {
  opacity: 0;

  transform: translateY(-4px);
}

/* Dark mode */

html[data-theme="dark"] .step-card {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(54, 132, 255, 0.09),
      transparent 250px
    ),
    #1c1c1e;
}

html[data-theme="dark"] .step-title {
  color: #f2f2f4;
}

html[data-theme="dark"] .step-label {
  color: #81858e;
}

html[data-theme="dark"] .optional-badge {
  color: #a3aab6;

  background: rgba(255, 255, 255, 0.055);
  border-color: rgba(255, 255, 255, 0.07);
}

html[data-theme="dark"] .description {
  color: #a7aab1;
}

html[data-theme="dark"] .selection-counter {
  color: #959ca8;
  background: #292a2f;

  border-color: #37383e;
}

html[data-theme="dark"] .selection-counter.active {
  color: #79aaf1;
  background: rgba(67, 136, 245, 0.12);

  border-color: rgba(67, 136, 245, 0.16);
}

html[data-theme="dark"] .selection-hint {
  color: #7f838b;
}

html[data-theme="dark"] .clear-button {
  color: #9aa1ac;
  background: transparent;

  border-color: #3d3f45;
}

html[data-theme="dark"] .clear-button:hover {
  color: #ff8b94;
  background: rgba(215, 80, 85, 0.1);

  border-color: rgba(215, 80, 85, 0.22);
}

html[data-theme="dark"] .perk {
  color: #d2d5db;
  background: #292a2e;

  border-color: #414349;

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.025);
}

html[data-theme="dark"] .perk:hover {
  color: #dfeaff;
  background: #2d2f34;

  border-color: rgba(82, 146, 239, 0.36);

  box-shadow:
    0 8px 21px rgba(0, 0, 0, 0.2);
}

html[data-theme="dark"] .perk:focus-visible {
  box-shadow:
    0 0 0 4px rgba(70, 126, 229, 0.16);
}

html[data-theme="dark"] .perk.selected {
  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #328bed,
      #3d70df 58%,
      #5364dc
    );

  border-color: transparent;

  box-shadow:
    0 9px 21px rgba(29, 75, 164, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.16);
}

html[data-theme="dark"] .perk.selected:hover {
  box-shadow:
    0 12px 25px rgba(29, 75, 164, 0.36);
}

html[data-theme="dark"] .perk-emoji {
  background: #343439;

  border-color: rgba(255, 255, 255, 0.07);

  box-shadow:
    0 4px 9px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .perk.selected .perk-emoji {
  background: rgba(255, 255, 255, 0.14);

  border-color: rgba(255, 255, 255, 0.15);
}

html[data-theme="dark"] .perk-check {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.18);

  border-color: rgba(255, 255, 255, 0.2);
}

html[data-theme="dark"] .selection-summary {
  color: #7fc499;

  background:
    linear-gradient(
      135deg,
      rgba(45, 155, 94, 0.1),
      rgba(43, 151, 116, 0.065)
    );

  border-color: rgba(50, 166, 102, 0.16);
}

html[data-theme="dark"] .selection-summary.neutral {
  color: #9ba2ae;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.1),
      rgba(83, 104, 232, 0.06)
    );

  border-color: rgba(74, 145, 246, 0.16);
}

html[data-theme="dark"] .summary-icon {
  color: #56cb8e;
  background: rgba(61, 192, 126, 0.14);
}

html[data-theme="dark"]
.selection-summary.neutral
.summary-icon {
  color: #67a2ff;
  background: rgba(67, 136, 245, 0.14);
}

html[data-theme="dark"] .summary-content strong {
  color: inherit;
}

html[data-theme="dark"] .summary-content span {
  color: #84918a;
}

html[data-theme="dark"]
.selection-summary.neutral
.summary-content
span {
  color: #858c98;
}

/* Compact laptop
   1366x768 / 1366x788
*/

@media (min-width: 721px) and (max-height: 820px) {
  .step-card {
    padding: 15px 22px;
  }

  .step-header {
    gap: 10px;

    margin-bottom: 8px;
  }

  .step-icon {
    width: 36px;
    height: 36px;

    border-radius: 11px;
  }

  .step-icon svg {
    width: 19px;
    height: 19px;
  }

  .step-heading {
    gap: 1px;
  }

  .step-label {
    font-size: 8px;
  }

  .step-title {
    font-size: 15px;
    line-height: 1.15;
  }

  .optional-badge {
    padding: 5px 8px;

    font-size: 8px;
  }

  .description {
    max-width: 680px;

    margin-bottom: 9px;

    font-size: 10px;
    line-height: 1.35;
  }

  .selection-panel {
    margin-bottom: 8px;
  }

  .selection-info {
    gap: 7px;
  }

  .selection-counter {
    min-height: 24px;

    gap: 3px;

    padding: 4px 8px;

    border-radius: 8px;

    font-size: 8px;
  }

  .counter-value {
    font-size: 10px;
  }

  .selection-hint {
    font-size: 8px;
  }

  .clear-button {
    gap: 4px;

    padding: 5px 8px;

    border-radius: 7px;

    font-size: 8px;
  }

  .clear-button svg {
    width: 12px;
    height: 12px;
  }

  .benefits-grid {
    gap: 6px;
  }

  .perk {
    min-height: 35px;

    gap: 6px;

    padding: 6px 9px;

    border-radius: 10px;

    font-size: 9px;
  }

  .perk-emoji {
    width: 21px;
    height: 21px;

    border-radius: 6px;

    font-size: 11px;
  }

  .perk-check {
    width: 16px;
    height: 16px;
  }

  .perk-check svg {
    width: 10px;
    height: 10px;
  }

  .selection-summary {
    gap: 8px;

    margin-top: 10px;
    padding: 8px 10px;

    border-radius: 10px;
  }

  .summary-icon {
    width: 27px;
    height: 27px;

    border-radius: 8px;
  }

  .summary-icon svg {
    width: 16px;
    height: 16px;
  }

  .summary-content {
    gap: 1px;
  }

  .summary-content strong {
    font-size: 9px;
  }

  .summary-content span {
    font-size: 8px;
    line-height: 1.3;
  }
}

/* Extra compact 1366x768 */

@media (min-width: 721px) and (max-height: 770px) {
  .step-card {
    padding:
      12px
      22px;
  }

  .step-header {
    margin-bottom: 6px;
  }

  .step-icon {
    width: 34px;
    height: 34px;
  }

  .step-icon svg {
    width: 18px;
    height: 18px;
  }

  .step-title {
    font-size: 14px;
  }

  .description {
    margin-bottom: 6px;

    font-size: 9.5px;
    line-height: 1.25;
  }

  .selection-panel {
    margin-bottom: 6px;
  }

  .selection-counter {
    min-height: 21px;

    padding:
      3px
      7px;
  }

  .selection-hint {
    font-size: 7.5px;
  }

  .clear-button {
    padding:
      4px
      7px;
  }

  .benefits-grid {
    gap: 5px;
  }

  .perk {
    min-height: 31px;

    gap: 5px;

    padding:
      5px
      8px;

    border-radius: 9px;

    font-size: 8.5px;
  }

  .perk-emoji {
    width: 19px;
    height: 19px;

    font-size: 10px;
  }

  .perk-check {
    width: 14px;
    height: 14px;
  }

  .perk-check svg {
    width: 9px;
    height: 9px;
  }

  .selection-summary {
    margin-top: 7px;

    padding:
      6px
      9px;
  }

  .summary-icon {
    width: 24px;
    height: 24px;
  }

  .summary-icon svg {
    width: 14px;
    height: 14px;
  }

  .summary-content strong {
    font-size: 8.5px;
  }

  .summary-content span {
    font-size: 7.5px;
  }
}

/* Mobile */

@media (max-width: 720px) {
  .step-card {
    padding: 21px 18px;

    border-radius: 20px;
  }

  .step-icon {
    width: 40px;
    height: 40px;

    border-radius: 13px;
  }

  .step-title {
    font-size: 17px;
  }

  .description {
    font-size: 13px;
  }

  .perk {
    min-height: 43px;

    padding:
      8px
      11px;

    font-size: 11px;
  }

  .perk-emoji {
    width: 24px;
    height: 24px;

    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .optional-badge {
    display: none;
  }

  .selection-panel {
    align-items: flex-start;
  }

  .selection-info {
    align-items: flex-start;
    flex-direction: column;

    gap: 6px;
  }

  .selection-hint {
    padding-left: 2px;
  }

  .benefits-grid {
    display: grid;
    grid-template-columns: 1fr;
  }

  .perk {
    width: 100%;

    justify-content: flex-start;

    white-space: normal;
    text-align: left;
  }

  .perk-check {
    margin-left: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-card,
  .perk {
    animation: none;
    opacity: 1;
  }

  .perk,
  .perk-emoji,
  .perk-check,
  .perk-shine,
  .clear-button,
  .counter-enter-active,
  .counter-leave-active,
  .clear-enter-active,
  .clear-leave-active,
  .summary-enter-active,
  .summary-leave-active {
    transition: none;
  }
}
</style>